// Bucket board (v0.4): like the status board, but columns are the collection's buckets (named,
// ordered, independent of status) plus a "No bucket" column when some cards have none.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Binding, Bucket, CreateItem, Item, Status } from "../api/types";
import { Modal } from "../components/bits";
import { MenuButton, useContextMenu } from "../components/ContextMenu";
import { bucketBadges, deleteBucketPlan } from "../lib/bucketRemote";
import { bucketColumns, bucketReorder, NO_BUCKET, nextBucketPosition } from "../lib/kanban";
import {
  createBucket,
  deleteBucket,
  loadConnectors,
  moveBucket,
  moveItem,
  renameBucket,
  toastError,
} from "../store/app";
import { useApp } from "../store/hooks";
import { Board, type BoardColumnDef } from "./BoardView";
import { toggleDone } from "./ListView";

function useManifests() {
  const manifests = useApp((s) => s.connectors);
  useEffect(() => {
    void loadConnectors();
  }, []);
  return manifests;
}

/** Inline name input: Enter saves, Esc / empty blur cancels. */
function NameInput({
  initial,
  label,
  onSubmit,
  onDone,
}: {
  initial: string;
  label: string;
  onSubmit: (name: string) => Promise<void>;
  onDone: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const commit = async () => {
    const v = value.trim();
    if (!v || v === initial) return onDone();
    setBusy(true);
    try {
      await onSubmit(v);
      onDone();
    } catch (e) {
      toastError(e, "Could not save bucket");
      setBusy(false);
      ref.current?.focus();
    }
  };
  return (
    <form
      className="bucket-name-form"
      onSubmit={(e) => {
        e.preventDefault();
        void commit();
      }}
    >
      <input
        ref={ref}
        aria-label={label}
        value={value}
        disabled={busy}
        placeholder="Bucket name"
        onChange={(e) => setValue(e.target.value)}
        // Don't start a column drag when selecting text.
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onDone();
          }
        }}
        onBlur={() => !busy && void commit()}
      />
    </form>
  );
}

function BucketHead({
  bucket,
  count,
  bindings,
  onDelete,
}: {
  bucket: Bucket;
  count: number;
  bindings: Binding[];
  onDelete: () => void;
}) {
  const manifests = useManifests();
  const [renaming, setRenaming] = useState(false);
  const menu = useContextMenu(
    () => [
      { label: "Rename", icon: "edit", onSelect: () => setRenaming(true) },
      { separator: true },
      { label: "Delete…", icon: "trash", danger: true, onSelect: onDelete },
    ],
    `${bucket.name} actions`,
  );
  const badges = bucketBadges(bucket, bindings, manifests);
  if (renaming) {
    return (
      <NameInput
        initial={bucket.name}
        label="Bucket name"
        onSubmit={async (name) => void (await renameBucket(bucket, name))}
        onDone={() => setRenaming(false)}
      />
    );
  }
  return (
    <div className={`bucket-head${menu.isOpen ? " menu-open" : ""}`} {...menu.targetProps}>
      <div className="bucket-head-main">
        <h2 onDoubleClick={() => setRenaming(true)} title="Double-click to rename">
          {bucket.name}
        </h2>
        <span className="count">{count}</span>
        <MenuButton label={`${bucket.name} actions`} expanded={menu.isOpen} onOpen={menu.openFrom} />
      </div>
      {badges.length > 0 && (
        <div className="bucket-badges">
          {badges.map((b) => (
            <span key={b.key} className="badge tiny" title={b.title}>
              {b.label}
            </span>
          ))}
        </div>
      )}
      {menu.menu}
    </div>
  );
}

function AddBucket({ collectionId, buckets }: { collectionId: string; buckets: Bucket[] }) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="column add-bucket">
      {adding ? (
        <div className="column-head">
          <NameInput
            initial=""
            label="New bucket name"
            onSubmit={async (name) => void (await createBucket(collectionId, name, nextBucketPosition(buckets)))}
            onDone={() => setAdding(false)}
          />
        </div>
      ) : (
        <button type="button" className="column-add-btn" onClick={() => setAdding(true)}>
          + Add bucket
        </button>
      )}
    </div>
  );
}

export function DeleteBucketDialog({
  bucket,
  buckets,
  items,
  bindings,
  onClose,
}: {
  bucket: Bucket;
  buckets: Bucket[];
  items: Item[];
  bindings: Binding[];
  onClose: () => void;
}) {
  const manifests = useManifests();
  const plan = useMemo(
    () => deleteBucketPlan(bucket, buckets, items, bindings, manifests),
    [bucket, buckets, items, bindings, manifests],
  );
  const [target, setTarget] = useState<string>(plan.defaultTarget ?? "");
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={`Delete bucket “${bucket.name}”?`} onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            await deleteBucket(bucket, plan.count ? target || null : null);
            onClose();
          } catch (err) {
            toastError(err, "Delete failed");
            setBusy(false);
          }
        }}
      >
        {plan.count > 0 ? (
          <label className="field inline-field">
            <span>
              Move {plan.count} task{plan.count === 1 ? "" : "s"} to:
            </span>
            <select value={target} onChange={(e) => setTarget(e.target.value)} autoFocus>
              {plan.targets.map((t) => (
                <option key={t.value ?? ""} value={t.value ?? ""}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p>The bucket is empty.</p>
        )}
        {plan.remote.length > 0 && (
          <ul className="binding-effects">
            {plan.remote.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        )}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn danger-solid" disabled={busy} autoFocus={plan.count === 0}>
            {busy ? "Deleting…" : "Delete bucket"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function BucketBoardView({
  collectionId,
  statuses,
  items,
  buckets,
  bindings,
  onCreate,
}: {
  collectionId: string;
  statuses: Status[];
  items: Item[];
  buckets: Bucket[];
  bindings: Binding[];
  onCreate: (body: CreateItem) => Promise<unknown>;
}) {
  const [deleting, setDeleting] = useState<Bucket | null>(null);
  const doneBox = useCallback(
    (item: Item) => (
      <input
        type="checkbox"
        className="row-check card-check"
        checked={item.status_category === "done"}
        aria-label={item.status_category === "done" ? `Reopen ${item.title}` : `Complete ${item.title}`}
        onPointerDown={(e) => e.stopPropagation()}
        onChange={() => toggleDone(item, statuses)}
      />
    ),
    [statuses],
  );
  const { keys, columns: cards } = useMemo(() => bucketColumns(buckets, items), [buckets, items]);
  const columns = useMemo<BoardColumnDef[]>(
    () =>
      keys.map((key) => {
        const bucket = buckets.find((b) => b.id === key);
        if (!bucket) return { key: NO_BUCKET, title: "No bucket", category: "backlog" };
        return {
          key,
          title: bucket.name,
          sortable: true,
          renderHead: (count: number) => (
            <BucketHead bucket={bucket} count={count} bindings={bindings} onDelete={() => setDeleting(bucket)} />
          ),
        };
      }),
    [keys, buckets, bindings],
  );

  return (
    <>
      <Board
        columns={columns}
        cards={cards}
        items={items}
        quickAdd={{ statuses, buckets }}
        onCreate={(key, body) => onCreate({ bucket_id: key === NO_BUCKET ? null : key, ...body })}
        onMoveCard={(id, key, m) =>
          void moveItem(id, {
            bucket_id: key === NO_BUCKET ? null : key,
            after_id: m.afterId,
            before_id: m.beforeId,
            position: m.position,
          }).catch((err) => toastError(err, "Move failed"))
        }
        onReorderColumn={(activeKey, overKey) => {
          const r = bucketReorder(buckets, activeKey, overKey);
          if (r) void moveBucket(r.bucket, r.position, r.order).catch((err) => toastError(err, "Reorder failed"));
        }}
        cardLead={doneBox}
        trailing={<AddBucket collectionId={collectionId} buckets={buckets} />}
      />
      {buckets.length === 0 && (
        <p className="muted small pad">No buckets yet. Add one with “+ Add bucket”.</p>
      )}
      {deleting && (
        <DeleteBucketDialog
          bucket={deleting}
          buckets={buckets}
          items={items}
          bindings={bindings}
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}
